import React, { useEffect, useState } from "react";
import { ReportAPI } from "../../api/apiClient";
import { WorksQtyReportRow } from "../../types/worksQtyReport";
import { useUI } from "../../context/UIContext";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

interface Props {
  projectId: number;
}

export default function WorksQtyReport({ projectId }: Props) {
  const { selectedProjectName } = useUI();

  const today = new Date().toISOString().split("T")[0];

  const [data, setData] = useState<WorksQtyReportRow[]>([]);
  const [loading, setLoading] = useState(false);

  const [fromDate, setFromDate] = useState<string | null>(null);
  const [toDate, setToDate] = useState<string>(today);

  const [usedFromDate, setUsedFromDate] = useState<string | null>(null);
  const [usedToDate, setUsedToDate] = useState<string | null>(null);

  const formatNumber = (value?: number) =>
    value !== undefined && value !== null
      ? Number(value).toFixed(2)
      : "0.00";

  const calculatePercent = (
    total?: number,
    previous?: number,
    achieved?: number
  ) => {
    const cumulative = (previous ?? 0) + (achieved ?? 0);
    if (!total || total === 0) return 0;
    return (cumulative / total) * 100;
  };

  const calculatePreviousPercent = (
    total?: number,
    previous?: number
  ) => {
    if (!total || total === 0) return 0;
    return ((previous ?? 0) / total) * 100;
  };

  const groupData = (rows: WorksQtyReportRow[]) => {
    const grouped: Record<string, WorksQtyReportRow[]> = {};
    rows.forEach((row) => {
      const key = row.workGroup;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(row);
    });
    return grouped;
  };

  const fetchReport = async () => {
    try {
      setLoading(true);
      setData([]);

      const result = await ReportAPI.getWorksQtyReport(
        -projectId,
        fromDate ?? null,
        toDate ?? null,
        0,
        0,
        1
      );

    /*   setData(result.data);
      setUsedFromDate(result.usedQDate);
      setUsedToDate(result.usedEndDate); */
      setData(result.data);

setUsedFromDate(result.usedQDate);
setUsedToDate(result.usedEndDate);

// Set default From Date only on first load
if (!fromDate && result.usedQDate) {
  setFromDate(result.usedQDate.split("T")[0]);
}
    } catch (error) {
      console.error("WorksQtyReport error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projectId) fetchReport();
  }, [projectId]);

  const extractNumbers = (text: string) => {
    const mainMatch = text.match(/^\d+/);
    const mainNumber = mainMatch ? parseInt(mainMatch[0]) : 0;

    const subMatch = text
      .split(">")
      .pop()
      ?.trim()
      .match(/^\d+/);

    const subNumber = subMatch ? parseInt(subMatch[0]) : 0;

    return { mainNumber, subNumber };
  };

  const groupedEntries = Object.entries(groupData(data)).sort(
    ([a], [b]) => {
      const numA = extractNumbers(a);
      const numB = extractNumbers(b);

      if (numA.mainNumber !== numB.mainNumber) {
        return numA.mainNumber - numB.mainNumber;
      }

      return numA.subNumber - numB.subNumber;
    }
  );

  const exportPDF = () => {
    const doc = new jsPDF("landscape");

    const now = new Date();
    const generatedOn = now.toLocaleString("en-GB");

    doc.setFontSize(16);
    doc.text("Works Progress Report", 148, 15, { align: "center" });

    doc.setFontSize(12);
    doc.text(selectedProjectName || "", 148, 23, { align: "center" });

    if (usedFromDate && usedToDate) {
      doc.text(
        `From ${new Date(usedFromDate).toLocaleDateString("en-GB")} 
To ${new Date(usedToDate).toLocaleDateString("en-GB")}`,
        148,
        30,
        { align: "center" }
      );
    }

    doc.setFontSize(10);
    doc.text(`Generated On: ${generatedOn}`, 14, 38);

    const tableRows: any[] = [];

    groupedEntries.forEach(([groupName, rows]) => {
      tableRows.push([
        {
          content: groupName,
          colSpan: 5,
          styles: { fontStyle: "bold", fillColor: [220, 220, 220] }
        }
      ]);

      rows.forEach((row) => {
        const percent = calculatePercent(
          row.quantity,
          row.previous,
          row.achieved
        );

        const prevPercent = calculatePreviousPercent(
          row.quantity,
          row.previous
        );

        tableRows.push([
          row.work,
          row.unitName?.trim(),
          formatNumber(row.quantity),
          row.previous ? formatNumber(row.previous) : "",
          row.achieved
            ? `${formatNumber(row.achieved)}|${percent.toFixed(0)}%|light`
            : `-|${prevPercent.toFixed(0)}%|dark`
        ]);
      });
    });

    autoTable(doc, {
      startY: 42,
      head: [["Work", "Unit", "Total", "Previous", "Achieved"]],
      body: tableRows,
      styles: { fontSize: 9 },
      headStyles: { fillColor: [55, 65, 81] },

      didParseCell: function (data) {
        if (data.column.index === 4 && data.cell.raw) {
          const raw = data.cell.raw.toString();
          const [value, percentText, type] = raw.split("|");

          data.cell.text = [value];
          (data.cell as any).percentText = percentText;
          (data.cell as any).badgeType = type;
        }
      },

      didDrawCell: function (data) {
        if (data.column.index === 4 && (data.cell as any).percentText) {

          const percentText = (data.cell as any).percentText;
          const type = (data.cell as any).badgeType;

          const { x, y, height, width } = data.cell;

          const textWidth = doc.getTextWidth(percentText);

          const paddingX = 3;
          const badgeHeight = 6;
          const badgeWidth = textWidth + paddingX * 2;

          const badgeX = x + width - badgeWidth - 4;
          const badgeY = y + (height - badgeHeight) / 2 - 1;

          if (type === "light") {
            doc.setFillColor(134, 239, 172);
            doc.setTextColor(0, 0, 0);
          } else {
            doc.setFillColor(21, 128, 61);
            doc.setTextColor(255, 255, 255);
          }

          doc.roundedRect(
            badgeX,
            badgeY,
            badgeWidth,
            badgeHeight,
            2,
            2,
            "F"
          );

          doc.setFontSize(8);

          doc.text(
            percentText,
            badgeX + badgeWidth / 2,
            badgeY + badgeHeight / 2 + 2,
            { align: "center" }
          );

          doc.setTextColor(0, 0, 0);
        }
      },

      didDrawPage: function (data) {
        const pageCount = doc.getNumberOfPages();
        const pageSize = doc.internal.pageSize;
        const pageHeight = pageSize.height;

        doc.setFontSize(9);
        doc.text(
          `Page ${data.pageNumber} of ${pageCount}`,
          pageSize.getWidth() / 2,
          pageHeight - 8,
          { align: "center" }
        );
      }
    });

    doc.save("Works_Progress_Report.pdf");
  };

  return (
    <div className="bg-white p-10 text-sm text-black max-w-[1100px] mx-auto">

      <div className="text-center mb-6">
        <div className="text-lg font-semibold">
          Works Progress Report
        </div>
        <div>{selectedProjectName}</div>

        <div>
          {usedFromDate && usedToDate
            ? `From ${new Date(usedFromDate).toLocaleDateString("en-GB")} 
               To ${new Date(usedToDate).toLocaleDateString("en-GB")}`
            : ""}
        </div>
      </div>

      <div className="flex gap-4 justify-center mb-6">
        <input
          type="date"
          value={fromDate ?? ""}
          onChange={(e) => setFromDate(e.target.value || null)}
          className="border px-2 py-1"
        />

        <input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          className="border px-2 py-1"
        />

        <button
          onClick={fetchReport}
          className="bg-blue-600 text-white px-4 py-1"
        >
          Generate
        </button>

        <button
          onClick={exportPDF}
          className="bg-green-600 text-white px-4 py-1"
        >
          Export PDF
        </button>
      </div>

      <div className="grid grid-cols-[4fr_1fr_1fr_1fr_1fr] bg-gray-700 text-white px-4 py-2 font-semibold">
        <div>Work</div>
        <div className="text-center">Unit</div>
        <div className="text-right">Total</div>
        <div className="text-right">Previous</div>
        <div className="text-right">Achieved</div>
      </div>

      {loading ? (
        <div className="text-center mt-6 text-blue-600 font-semibold">
          Loading...
        </div>
      ) : (
        groupedEntries.map(([groupName, rows], index) => (
          <div key={index}>
            <div className="bg-gray-300 px-4 py-2 font-semibold mt-4">
              {groupName}
            </div>

            {rows.map((row, i) => {

              const percent = calculatePercent(
                row.quantity,
                row.previous,
                row.achieved
              );

              const prevPercent = calculatePreviousPercent(
                row.quantity,
                row.previous
              );

              return (
                <div
                  key={i}
                  className="grid grid-cols-[4fr_1fr_1fr_1fr_1fr] px-4 py-2 border-b"
                >
                  <div className="pl-6">{row.work}</div>

                  <div className="text-center">
                    {row.unitName?.trim()}
                  </div>

                  <div className="text-right">
                    {formatNumber(row.quantity)}
                  </div>

                  <div className="text-right">
                    {row.previous ? formatNumber(row.previous) : ""}
                  </div>

                  <div className="text-right">

                    {row.achieved ? formatNumber(row.achieved) : "-"}

                    <span
                      className={`text-xs px-2 py-0.5 rounded ml-2 ${
                        row.achieved
                          ? "bg-green-300 text-black"
                          : "bg-green-700 text-white"
                      }`}
                    >
                      {row.achieved
                        ? percent.toFixed(0)
                        : prevPercent.toFixed(0)}%
                    </span>

                  </div>

                </div>
              );
            })}
          </div>
        ))
      )}
    </div>
  );
}